import { escapeCsvField } from './csvExport';
import { TAX_PIPELINE } from './taxPipelineConfig';
import type { LedgerEvent } from './types';

/** One auditable row (Crypto.com CSV, live Cronos fetch, or compiled explorer ledger). */
export type FiCsvDiagnosticRow = {
  source:
    | 'crypto_com'
    | 'cronos'
    | 'arbitrum'
    | 'explorer_compiled'
    | 'pipeline'
    | 'binance'
    | 'coinbase';
  csv_row: number;
  ref: string;
  transaction_kind: string;
  asset: string;
  category: 'NON_TAXABLE_INTERNAL' | 'AMBIGUOUS_REVIEW' | 'INCOME_PIPELINE';
  subreason: string;
  note: string;
};

/** Human-readable CSV for audit / review (internal moves, ambiguous routing). */
export function buildFiDiagnosticsCsv(rows: FiCsvDiagnosticRow[]): string {
  const headers = [
    'policy_mode',
    'basis_method',
    'timezone',
    'source',
    'csv_row',
    'ref',
    'transaction_kind',
    'asset',
    'category',
    'subreason',
    'note',
  ];
  const meta = TAX_PIPELINE.fi;
  const lines = [headers.map(escapeCsvField).join(',')];
  for (const r of rows) {
    lines.push(
      [
        meta.policyMode,
        meta.basisMethod,
        meta.timezone,
        r.source,
        String(r.csv_row),
        r.ref,
        r.transaction_kind,
        r.asset,
        r.category,
        r.subreason,
        r.note,
      ]
        .map(escapeCsvField)
        .join(',')
    );
  }
  return lines.join('\r\n');
}

/**
 * Live Cronos: one diagnostic per non-tax internal transfer; optional row for unclear ownership
 * (`UNKNOWN_REVIEW` + `unclear_ownership`).
 */
export function appendCronosFiDiagnostic(diagnostics: FiCsvDiagnosticRow[], ev: LedgerEvent): void {
  if (ev.source !== 'cronos') return;
  if (ev.kind === 'internal_transfer') {
    diagnostics.push({
      source: 'cronos',
      csv_row: 0,
      ref: ev.ref ?? '',
      transaction_kind: String(ev.classification ?? 'INTERNAL_TRANSFER'),
      asset: ev.asset,
      category: 'NON_TAXABLE_INTERNAL',
      subreason: ev.fiSubreason ?? 'CRONOS_OWNED_TO_OWNED',
      note: ev.explanation ?? '',
    });
    return;
  }
  if (ev.classification === 'UNKNOWN_REVIEW' && ev.reviewFlags?.includes('unclear_ownership')) {
    diagnostics.push({
      source: 'cronos',
      csv_row: 0,
      ref: ev.ref ?? '',
      transaction_kind: 'UNKNOWN_REVIEW',
      asset: ev.asset,
      category: 'AMBIGUOUS_REVIEW',
      subreason: 'CRONOS_UNCLEAR_OWNERSHIP',
      note: ev.explanation ?? '',
    });
  }
}

/** Same as {@link appendCronosFiDiagnostic} for Arbitrum explorer imports. */
export function appendArbitrumFiDiagnostic(diagnostics: FiCsvDiagnosticRow[], ev: LedgerEvent): void {
  if (ev.source !== 'arbitrum') return;
  if (ev.kind === 'internal_transfer') {
    diagnostics.push({
      source: 'arbitrum',
      csv_row: 0,
      ref: ev.ref ?? '',
      transaction_kind: String(ev.classification ?? 'INTERNAL_TRANSFER'),
      asset: ev.asset,
      category: 'NON_TAXABLE_INTERNAL',
      subreason: ev.fiSubreason ?? 'ARBITRUM_OWNED_TO_OWNED',
      note: ev.explanation ?? '',
    });
    return;
  }
  if (ev.classification === 'UNKNOWN_REVIEW' && ev.reviewFlags?.includes('unclear_ownership')) {
    diagnostics.push({
      source: 'arbitrum',
      csv_row: 0,
      ref: ev.ref ?? '',
      transaction_kind: 'UNKNOWN_REVIEW',
      asset: ev.asset,
      category: 'AMBIGUOUS_REVIEW',
      subreason: 'ARBITRUM_UNCLEAR_OWNERSHIP',
      note: ev.explanation ?? '',
    });
  }
}
